import { collectModuleFlowStatements, consumeThroughLineBreak, offsetAtCodeLineColumn, splitCodeLines } from './pythonSourceScanUtils';

export const UPDATE_MONITOR_DATA_CALL = 'DataStreamStore.update_monitor_data()';
/** Matches the class-level call and the legacy `get_instance()` form, so both stay protected. */
const UPDATE_MONITOR_DATA_CALL_LINE_RE = /^\s*DataStreamStore\s*\.\s*(?:get_instance\s*\(\s*\)\s*\.\s*)?update_monitor_data\s*\(\s*\)\s*$/;

export type UpdateMonitorDataCallRange = {
  startLine: number;
  endLine: number;
  sourceText: string;
  start: number;
  end: number;
};

/** Finds canonical standalone monitor-registration statements outside Python strings, comments, and continuations. */
export function findUpdateMonitorDataCallRanges(code: string): UpdateMonitorDataCallRange[] {
  const lines = splitCodeLines(code);
  const ranges: UpdateMonitorDataCallRange[] = [];
  const standaloneStatementLines = new Set(
    collectModuleFlowStatements(code, { includeDefSuites: true })
      .filter((statement) => statement.startLineIndex === statement.endLineIndex)
      .map((statement) => statement.startLineIndex)
  );

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex] ?? '';
    if (!standaloneStatementLines.has(lineIndex) || !UPDATE_MONITOR_DATA_CALL_LINE_RE.test(line)) continue;

    const start = offsetAtCodeLineColumn(code, lineIndex, 0);

    ranges.push({
      startLine: lineIndex + 1,
      endLine: lineIndex + 1,
      sourceText: line,
      start,
      end: consumeThroughLineBreak(code, start + line.length),
    });
  }

  return ranges;
}

/** True when an existing protected call was edited, removed, or duplicated. */
export function hasProtectedUpdateMonitorDataCallChange(previousCode: string, nextCode: string): boolean {
  const previous = findUpdateMonitorDataCallRanges(previousCode).map(({ sourceText }) => sourceText);
  if (previous.length === 0) return false;
  const next = findUpdateMonitorDataCallRanges(nextCode).map(({ sourceText }) => sourceText);
  if (previous.length !== next.length) return true;

  previous.sort();
  next.sort();
  return previous.some((sourceText, index) => sourceText !== next[index]);
}
